/**
 * Checks if a redirect URL is valid by ensuring it is a well-formed URL and does not point to the same normalized path as the current URL.
 *
 * The function parses and normalizes both URLs by decoding their paths, converting to lowercase, and removing trailing slashes (except for root).
 * It returns false if the redirect URL matches the current URL after normalization, or if either URL is invalid.
 *
 * @param {string} redirectUrlString - The target URL for redirection.
 * @param {string} currentUrlString - The current page URL.
 * @returns {boolean} `true` if the redirect URL is valid and not the same as the current URL; otherwise, `false`.
 */
export const isValidRedirectURL = (redirectUrlString, currentUrlString) => {
  const normalizeUrl = (url) => {
    // Remove hash and search params, normalize path
    let path = decodeURIComponent(url.pathname);
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    return (
      url.protocol.toLowerCase() +
      '//' +
      url.host.toLowerCase() +
      path.toLowerCase()
    );
  };
  try {
    const currentUrl = new URL(currentUrlString);
    const redirectUrl = new URL(redirectUrlString);
    // Both redirectUrl and currentUrl are URL objects
    const normalizedCurrent = normalizeUrl(currentUrl);
    const normalizedRedirect = normalizeUrl(redirectUrl);
    if (normalizedCurrent === normalizedRedirect) {
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};
